import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Sentry from "@Sentry";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { useTranslation, type TranslatedString } from "@i18n";
import IconComponent from "@CubeComponents/icons";
import MenuComponent from "@CubeComponents/html/MenuComponent";
import MenuItemComponent from "@CubeComponents/html/MenuItemComponent";
import { RootState } from "@Reducers/index";
import { showToastNotification } from "@Reducers/toastSlice";
import { setShowPortalChat, setPortalChatUnreadCount } from "../../../reducers/portalChat";
import {
  getConversationsRequest,
  getMessagesRequest,
  sendMessageRequest,
  editMessageRequest,
  deleteMessageRequest,
  toggleReactionRequest,
  markConversationReadRequest,
  updateConversationStateRequest,
  deleteAttachmentRequest,
  getUnreadSummaryRequest,
  type ChatConversation,
  type ChatSection,
  type ChatMessage,
} from "../api/portalChat.api";
import { toObjectIdString } from "@Helpers/schemas/common.schema";
import { usePortalChatSocket } from "../hooks/usePortalChatSocket";
import {
  SCOPE_STORAGE_KEY,
  LAST_CONVERSATION_STORAGE_KEY,
  TYPING_TTL_MS,
  TYPING_SWEEP_INTERVAL_MS,
  BADGE_REFRESH_DEBOUNCE_MS,
  PREVIEW_LENGTH,
  UNREAD_COUNT_CAP,
  THREAD_WINDOW_SIZE,
  translateServerError,
} from "../portalChat.constants";
import {
  PortalChatContext,
  type PortalChatContextValue,
  type ThreadData,
  type ComposerIntent,
  type ChatScope,
} from "./context";
import ChatListPane from "./ChatListPane";
import ChatThreadPane from "./ChatThreadPane";
import { ForwardDialog, ReceiptsDialog, ReactionsDialog, EditMessageDialog } from "./ChatDialogs";

/**
 * Portal Chat — the slide-out panel shell (PRD §panel): floating above the page, anchored to the
 * end/bottom with a 14px gap, maximize to full-bleed below the header, close resets maximize.
 * Mounted once at Router level (like the Notification panel) so it survives route changes.
 */
function PortalChatPanel() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const show = useSelector((state: RootState) => state.showPortalChat);
  const userDetails = useSelector((state: RootState) => state.userDetails);
  const userId = String(userDetails._id ?? userDetails.id ?? "");
  const userName = `${userDetails.firstName ?? ""} ${userDetails.lastName ?? ""}`.trim();

  const [maximized, setMaximized] = useState(false);
  const [scope, setScopeState] = useState<ChatScope>(() => {
    // PRD §4.7: the last picked scope persists per tab (the open-effect below overrides to
    // Project Chats when the panel is opened from inside a project).
    try {
      const stored = sessionStorage.getItem(SCOPE_STORAGE_KEY);
      if (stored === "all" || stored === "direct" || stored === "group" || stored === "project") return stored;
    } catch {
      // sessionStorage unavailable — default below.
    }
    return "all";
  });
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [scopeMenuAnchor, setScopeMenuAnchor] = useState<HTMLElement | null>(null);
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [sections, setSections] = useState<ChatSection[]>([]);
  const [projectOrder, setProjectOrder] = useState<string[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [connected, setConnected] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [thread, setThread] = useState<ThreadData | null>(null);
  const [composerIntent, setComposerIntent] = useState<ComposerIntent>(null);
  const [highlightMessageId, setHighlightMessageId] = useState<string | null>(null);
  const [typingByConversation, setTypingByConversation] = useState<Record<string, Record<string, { until: number }>>>(
    {},
  );
  const [forwardMessage, setForwardMessage] = useState<ChatMessage | null>(null);
  const [receiptsMessage, setReceiptsMessage] = useState<ChatMessage | null>(null);
  const [reactionsMessage, setReactionsMessage] = useState<ChatMessage | null>(null);
  const [editDialogMessage, setEditDialogMessage] = useState<ChatMessage | null>(null);
  const [menuEpoch, setMenuEpoch] = useState(0);

  const selectedIdRef = useRef<string | null>(null);
  selectedIdRef.current = selectedId;
  const showRef = useRef(show);
  showRef.current = show;
  const summaryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevConnectedRef = useRef(true);

  const { pathname } = useLocation();
  const currentProjectId = useMemo(() => {
    if (!show) return null;
    const match = /\/project\/([0-9a-fA-F]{24})/.exec(pathname);
    return match?.[1] ?? null;
  }, [show, pathname]);

  const showToast = useCallback(
    (content: TranslatedString, success = true) => {
      dispatch(showToastNotification({ content, type: success ? "success" : "error" }));
    },
    [dispatch],
  );

  /** Server errors arrive as fixed English sentences — map them through i18n before toasting. */
  const showServerError = useCallback(
    (serverMessage: string | undefined, fallback: TranslatedString) =>
      showToast(translateServerError(t, serverMessage, fallback), false),
    [showToast, t],
  );

  const publishBadge = useCallback(
    (rows: ChatConversation[]) => {
      // Muted AND hidden conversations are excluded from the icon badge (PRD §4.5/§4.11).
      const count = rows.filter((c) => !c.muted && !c.hidden && c.unreadCount > 0).length;
      dispatch(setPortalChatUnreadCount(count));
    },
    [dispatch],
  );

  const refreshList = useCallback(async () => {
    try {
      const response = await getConversationsRequest();
      if (response.data.status) {
        const rows = response.data.result ?? [];
        setConversations(rows);
        setSections(response.data.sections ?? []);
        setProjectOrder((response.data.projectOrder ?? []).map(String));
        publishBadge(rows);
      }
    } catch (error) {
      Sentry.captureException(error);
      showToast(t("Could not load chats"), false);
    }
  }, [publishBadge, showToast, t]);

  const markRead = useCallback(
    (conversationId: string) => {
      void markConversationReadRequest({ conversationId: toObjectIdString(conversationId) }).catch((error: unknown) =>
        Sentry.captureException(error),
      );
      setConversations((prev) => {
        const next = prev.map((c) =>
          c._id === conversationId ? { ...c, unreadCount: 0, lastReadDate: Date.now() } : c,
        );
        publishBadge(next);
        return next;
      });
    },
    [publishBadge],
  );

  const selectConversation = useCallback(
    (conversationId: string | null) => {
      setSelectedId(conversationId);
      setComposerIntent(null);
      setHighlightMessageId(null);
      try {
        if (conversationId) sessionStorage.setItem(LAST_CONVERSATION_STORAGE_KEY, conversationId);
      } catch {
        // sessionStorage may be unavailable; last-conversation restore is cosmetic.
      }
      if (!conversationId) {
        setThread(null);
        return;
      }
      setThread({ messages: [], members: [], memberReadDates: [], hasMore: false, loading: true });
      void getMessagesRequest({ conversationId: toObjectIdString(conversationId) })
        .then((response) => {
          if (selectedIdRef.current !== conversationId) return;
          if (response.data.status) {
            setThread({
              messages: response.data.result ?? [],
              members: response.data.members ?? [],
              memberReadDates: (response.data.memberReadDates ?? []).map((r) => ({
                userId: String(r.userId),
                lastReadDate: r.lastReadDate,
              })),
              hasMore: response.data.hasMore ?? false,
              loading: false,
            });
            markRead(conversationId);
          } else {
            setThread({ messages: [], members: [], memberReadDates: [], hasMore: false, loading: false, error: true });
          }
        })
        .catch((error: unknown) => {
          Sentry.captureException(error);
          if (selectedIdRef.current === conversationId) {
            setThread({ messages: [], members: [], memberReadDates: [], hasMore: false, loading: false, error: true });
          }
        });
    },
    [markRead],
  );

  // Open/close transitions (placed after selectConversation so the deps don't hit its TDZ).
  useEffect(() => {
    if (!show) return;
    setLoadingList(true);
    void refreshList().finally(() => setLoadingList(false));
    // PRD §4.7: opening from inside a project always lands on Project Chats, overriding the last
    // scope — but WITHOUT overwriting the persisted per-tab preference (setScopeState, not setScope).
    if (/\/project\/[0-9a-fA-F]{24}/.test(window.location.pathname)) setScopeState("project");
    // A selection retained from before the panel was closed may be stale — messages that arrived
    // while closed were never appended (closed-panel events only refresh the badge). Refetch it.
    if (selectedIdRef.current) selectConversation(selectedIdRef.current);
  }, [show, refreshList, selectConversation]);

  // PRD §4.1: the Chats icon opens the last conversation, or the empty state if none exists.
  useEffect(() => {
    if (!show || selectedIdRef.current) return;
    let lastId: string | null = null;
    try {
      lastId = sessionStorage.getItem(LAST_CONVERSATION_STORAGE_KEY);
    } catch {
      lastId = null;
    }
    if (lastId && conversations.some((c) => c._id === lastId && !c.hidden)) selectConversation(lastId);
    // eslint-disable-next-line react-hooks-ext/exhaustive-deps -- fire once per open after the list arrives; re-running on every list change would fight manual deselection
  }, [show, conversations.length]);

  /** Append a live/sent message, keeping at most THREAD_WINDOW_SIZE rows in the DOM — older
   *  rows re-page through loadOlderMessages, so hasMore flips back on when the window trims. */
  const appendToThread = (prev: ThreadData | null, message: ChatMessage): ThreadData | null => {
    if (!prev || prev.messages.some((m) => m._id === message._id)) return prev;
    const messages = [...prev.messages, message];
    if (messages.length <= THREAD_WINDOW_SIZE) return { ...prev, messages };
    return { ...prev, messages: messages.slice(messages.length - THREAD_WINDOW_SIZE), hasMore: true };
  };

  const loadOlderMessages = useCallback(async () => {
    const conversationId = selectedIdRef.current;
    if (!conversationId) return;
    let before: number | undefined;
    let beforeId: string | undefined;
    setThread((prev) => {
      before = prev?.messages[0]?.createdDate;
      beforeId = prev?.messages[0]?._id;
      return prev;
    });
    if (!before) return;
    const response = await getMessagesRequest({
      conversationId: toObjectIdString(conversationId),
      before,
      ...(beforeId ? { beforeId: toObjectIdString(beforeId) } : {}),
    });
    if (response.data.status && selectedIdRef.current === conversationId) {
      const older = response.data.result ?? [];
      setThread((prev) =>
        prev
          ? {
              ...prev,
              messages: [...older, ...prev.messages.filter((m) => !older.some((o) => o._id === m._id))],
              hasMore: response.data.status ? (response.data.hasMore ?? false) : prev.hasMore,
            }
          : prev,
      );
    }
  }, []);

  /** Panel closed: refresh just the badge, debounced — a busy space chat must not fan out
   *  one full getConversations per message per connected client. */
  const refreshBadgeDebounced = useCallback(() => {
    if (summaryTimerRef.current) return;
    summaryTimerRef.current = setTimeout(() => {
      summaryTimerRef.current = null;
      getUnreadSummaryRequest()
        .then((response) => {
          if (response.data.status) dispatch(setPortalChatUnreadCount(response.data.unreadConversations ?? 0));
        })
        .catch((error: unknown) => Sentry.captureException(error));
    }, BADGE_REFRESH_DEBOUNCE_MS);
  }, [dispatch]);

  const applyConversationPreview = useCallback(
    (conversationId: string, message: ChatMessage, incrementUnread: boolean) => {
      if (!showRef.current) {
        refreshBadgeDebounced();
        return;
      }
      setConversations((prev) => {
        const known = prev.some((c) => c._id === conversationId);
        if (!known) {
          void refreshList();
          return prev;
        }
        const next = prev.map((c) => {
          if (c._id !== conversationId) return c;
          const mentioned = (message.mentions ?? []).some((m) => String(m) === userId);
          return {
            ...c,
            lastMessageDate: message.createdDate,
            lastMessage: {
              messageId: message._id,
              senderId: message.senderId,
              senderName: message.senderName,
              kind: message.kind,
              text: (message.text ?? "").slice(0, PREVIEW_LENGTH),
              hasAttachments: message.attachments?.length ? true : undefined,
              deleted: message.deleted,
            },
            unreadCount: incrementUnread ? Math.min(c.unreadCount + 1, UNREAD_COUNT_CAP) : c.unreadCount,
            // Resurface parity with the server (PRD §4.11): direct/group return on a new message
            // unless muted (mention bypasses mute); hidden PROJECT chats stay hidden unless mentioned.
            hidden:
              c.hidden &&
              (c.type === "space" ? !mentioned : (c.muted && !mentioned) || String(message.senderId) === userId)
                ? c.hidden
                : false,
          };
        });
        publishBadge(next);
        return next;
      });
    },
    [publishBadge, refreshBadgeDebounced, refreshList, userId],
  );

  const { emitTyping: socketEmitTyping } = usePortalChatSocket(Boolean(userId), {
    onConnectionChange: setConnected,
    onMessageNew: ({ conversationId, message }) => {
      const own = String(message.senderId) === userId;
      const isOpen = selectedIdRef.current === conversationId && show;
      applyConversationPreview(conversationId, message, !own && !isOpen);
      if (isOpen) {
        setThread((prev) => appendToThread(prev, message));
        // Only an actually-visible thread counts as read — a background tab must not
        // consume read receipts for messages the user never saw.
        if (!own && document.visibilityState === "visible") markRead(conversationId);
      }
      setTypingByConversation((prev) => {
        const forConversation = { ...(prev[conversationId] ?? {}) };
        delete forConversation[String(message.senderId)];
        return { ...prev, [conversationId]: forConversation };
      });
    },
    onMessageEdited: ({ conversationId, message }) => {
      if (selectedIdRef.current === conversationId) {
        setThread((prev) =>
          prev ? { ...prev, messages: prev.messages.map((m) => (m._id === message._id ? message : m)) } : prev,
        );
      }
      setConversations((prev) =>
        prev.map((c) =>
          c._id === conversationId && c.lastMessage?.messageId === message._id
            ? { ...c, lastMessage: { ...c.lastMessage, text: (message.text ?? "").slice(0, PREVIEW_LENGTH) } }
            : c,
        ),
      );
    },
    onMessageDeleted: ({ conversationId, messageId }) => {
      if (selectedIdRef.current === conversationId) {
        setThread((prev) =>
          prev
            ? {
                ...prev,
                messages: prev.messages.map((m) =>
                  m._id === messageId
                    ? {
                        ...m,
                        deleted: true,
                        text: undefined,
                        attachments: undefined,
                        reactions: undefined,
                        replyTo: undefined,
                        forwardOf: undefined,
                      }
                    : m,
                ),
              }
            : prev,
        );
      }
      setConversations((prev) =>
        prev.map((c) =>
          c._id === conversationId && c.lastMessage?.messageId === messageId
            ? { ...c, lastMessage: { ...c.lastMessage, text: "", deleted: true } }
            : c,
        ),
      );
    },
    onReactionUpdated: ({ conversationId, messageId, reactions }) => {
      if (selectedIdRef.current !== conversationId) return;
      const branded = reactions.map((reaction) => ({
        emoji: reaction.emoji,
        users: reaction.users.map((id) => toObjectIdString(id)),
      }));
      setThread((prev) =>
        prev
          ? {
              ...prev,
              messages: prev.messages.map((m) => (m._id === messageId ? { ...m, reactions: branded } : m)),
            }
          : prev,
      );
    },
    onReadUpdated: ({ conversationId, userId: readerId, lastReadDate }) => {
      if (selectedIdRef.current !== conversationId) return;
      setThread((prev) =>
        prev
          ? {
              ...prev,
              memberReadDates: [
                ...prev.memberReadDates.filter((r) => r.userId !== readerId),
                { userId: readerId, lastReadDate },
              ],
            }
          : prev,
      );
    },
    onConversationUpdated: () => {
      if (!showRef.current) {
        refreshBadgeDebounced();
        return;
      }
      void refreshList();
      const conversationId = selectedIdRef.current;
      if (conversationId) {
        // Membership / name may have changed; refresh the open thread's members lazily.
        void getMessagesRequest({ conversationId: toObjectIdString(conversationId), limit: 1 }).then((response) => {
          if (response.data.status && selectedIdRef.current === conversationId) {
            setThread((prev) =>
              prev ? { ...prev, members: response.data.status ? (response.data.members ?? []) : prev.members } : prev,
            );
          }
        });
      }
    },
    onMemberRemoved: ({ conversationId }) => {
      if (!showRef.current) {
        // The list is empty/stale while closed — let the debounced summary recompute the badge.
        refreshBadgeDebounced();
        return;
      }
      // PRD §4.3: the conversation disappears immediately for the removed member, even if open;
      // the panel selects the next available conversation.
      setConversations((prev) => {
        const next = prev.filter((c) => c._id !== conversationId);
        publishBadge(next);
        if (selectedIdRef.current === conversationId) {
          const fallback = next.find((c) => !c.hidden);
          setTimeout(() => selectConversation(fallback ? fallback._id : null), 0);
        }
        return next;
      });
    },
    onTyping: ({ conversationId, userId: typerId, isTyping }) => {
      setTypingByConversation((prev) => {
        const forConversation = { ...(prev[conversationId] ?? {}) };
        if (isTyping) forConversation[typerId] = { until: Date.now() + TYPING_TTL_MS };
        else delete forConversation[typerId];
        return { ...prev, [conversationId]: forConversation };
      });
    },
  });

  // Sweep expired typing entries.
  useEffect(() => {
    const timer = setInterval(() => {
      setTypingByConversation((prev) => {
        const nowMs = Date.now();
        let changed = false;
        const next: typeof prev = {};
        for (const [conversationId, users] of Object.entries(prev)) {
          const alive = Object.fromEntries(Object.entries(users).filter(([, v]) => v.until > nowMs));
          if (Object.keys(alive).length !== Object.keys(users).length) changed = true;
          next[conversationId] = alive;
        }
        return changed ? next : prev;
      });
    }, TYPING_SWEEP_INTERVAL_MS);
    return () => clearInterval(timer);
  }, []);

  const sendMessage = useCallback<PortalChatContextValue["sendMessage"]>(
    async ({ text, attachments, replyToMessageId, forwardOfMessageId, mentions, conversationId: explicitId }) => {
      const conversationId = explicitId ?? selectedIdRef.current;
      if (!conversationId) return false;
      try {
        const response = await sendMessageRequest({
          conversationId: toObjectIdString(conversationId),
          ...(text ? { text } : {}),
          ...(attachments?.length ? { attachments } : {}),
          ...(replyToMessageId ? { replyToMessageId: toObjectIdString(replyToMessageId) } : {}),
          ...(forwardOfMessageId ? { forwardOfMessageId: toObjectIdString(forwardOfMessageId) } : {}),
          ...(mentions?.length ? { mentions: mentions.map((id) => toObjectIdString(id)) } : {}),
        });
        if (!response.data.status) {
          showServerError(response.data.errorMessage, t("Could not send the message"));
          return false;
        }
        const message = response.data.result;
        if (message) {
          // Guarded like every other async path: a reply that lands after the user switched
          // conversations must not append into the newly opened thread.
          if (selectedIdRef.current === conversationId) {
            setThread((prev) => appendToThread(prev, message));
          }
          applyConversationPreview(conversationId, message, false);
        }
        return true;
      } catch (error) {
        Sentry.captureException(error);
        showToast(t("Could not send the message"), false);
        return false;
      }
    },
    [applyConversationPreview, showServerError, showToast, t],
  );

  const editMessage = useCallback(
    async (messageId: string, text: string) => {
      const response = await editMessageRequest({ messageId: toObjectIdString(messageId), text });
      if (!response.data.status) {
        showServerError(response.data.errorMessage, t("Could not edit the message"));
        return false;
      }
      const message = response.data.result;
      if (message) {
        setThread((prev) =>
          prev ? { ...prev, messages: prev.messages.map((m) => (m._id === message._id ? message : m)) } : prev,
        );
      }
      showToast(t("Message edited."));
      return true;
    },
    [showServerError, showToast, t],
  );

  const deleteMessage = useCallback(
    async (messageId: string) => {
      const response = await deleteMessageRequest({ messageId: toObjectIdString(messageId) });
      if (!response.data.status) {
        showServerError(response.data.errorMessage, t("Could not delete the message"));
        return;
      }
      setThread((prev) =>
        prev
          ? {
              ...prev,
              messages: prev.messages.map((m) =>
                m._id === messageId
                  ? {
                      ...m,
                      deleted: true,
                      text: undefined,
                      attachments: undefined,
                      reactions: undefined,
                      replyTo: undefined,
                      forwardOf: undefined,
                    }
                  : m,
              ),
            }
          : prev,
      );
    },
    [showServerError, t],
  );

  const toggleReaction = useCallback(async (messageId: string, emoji: string) => {
    const response = await toggleReactionRequest({ messageId: toObjectIdString(messageId), emoji });
    if (response.data.status) {
      const reactions = response.data.reactions ?? [];
      setThread((prev) =>
        prev ? { ...prev, messages: prev.messages.map((m) => (m._id === messageId ? { ...m, reactions } : m)) } : prev,
      );
    }
  }, []);

  const updateConversationState = useCallback(
    async (conversationId: string, patch: { hidden?: boolean; muted?: boolean; sectionId?: string | null }) => {
      const response = await updateConversationStateRequest({
        conversationId: toObjectIdString(conversationId),
        hidden: patch.hidden,
        muted: patch.muted,
        ...(patch.sectionId !== undefined
          ? { sectionId: patch.sectionId === null ? null : toObjectIdString(patch.sectionId) }
          : {}),
      });
      if (!response.data.status) {
        showServerError(response.data.errorMessage, t("Something went wrong"));
        return;
      }
      setConversations((prev) => {
        const next = prev.map((c) =>
          c._id === conversationId
            ? {
                ...c,
                ...(patch.hidden !== undefined ? { hidden: patch.hidden } : {}),
                ...(patch.muted !== undefined ? { muted: patch.muted } : {}),
                ...(patch.sectionId !== undefined
                  ? { sectionId: patch.sectionId === null ? null : toObjectIdString(patch.sectionId) }
                  : {}),
              }
            : c,
        );
        publishBadge(next);
        return next;
      });
    },
    [publishBadge, showServerError, t],
  );

  const deleteAttachment = useCallback(
    async (messageId: string, awsKey: string) => {
      const response = await deleteAttachmentRequest({ messageId: toObjectIdString(messageId), awsKey });
      if (!response.data.status) {
        showServerError(response.data.errorMessage, t("Something went wrong"));
      }
      // The message:edited (or message:deleted) socket echo carries the updated state.
    },
    [showServerError, t],
  );

  const unhideAndOpen = useCallback(
    (conversationId: string) => {
      const conversation = conversations.find((c) => c._id === conversationId);
      if (conversation?.hidden) {
        void updateConversationState(conversationId, { hidden: false });
        showToast(t("{{name}} is back in your list.", { name: conversation.name }));
      }
      selectConversation(conversationId);
    },
    [conversations, selectConversation, showToast, t, updateConversationState],
  );

  const selectNextAfter = useCallback(
    (conversationId: string) => {
      const fallback = conversations.find((c) => c._id !== conversationId && !c.hidden);
      selectConversation(fallback ? fallback._id : null);
    },
    [conversations, selectConversation],
  );

  const jumpToMessage = useCallback(
    (conversationId: string, messageId: string) => {
      // A forward source may have left the viewer's list since the page was rendered
      // (space deleted, project archived, membership lost) — never navigate into a void.
      if (!conversations.some((c) => c._id === conversationId)) {
        showToast(t("This conversation is no longer available"), false);
        return;
      }
      if (selectedIdRef.current !== conversationId) selectConversation(conversationId);
      setHighlightMessageId(messageId);
    },
    [conversations, selectConversation, showToast, t],
  );

  const emitTyping = useCallback(
    (conversationId: string, isTyping: boolean) => socketEmitTyping(conversationId, isTyping),
    [socketEmitTyping],
  );

  // Messages sent while the socket was down were only ever broadcast live — on the
  // disconnected→connected transition, refetch so the thread has no silent hole.
  useEffect(() => {
    const was = prevConnectedRef.current;
    prevConnectedRef.current = connected;
    if (was || !connected) return;
    if (showRef.current) {
      void refreshList();
      if (selectedIdRef.current) selectConversation(selectedIdRef.current);
    } else {
      refreshBadgeDebounced();
    }
  }, [connected, refreshList, selectConversation, refreshBadgeDebounced]);

  const typingForSelected = selectedId ? typingByConversation[selectedId] : undefined;
  const threadMembers = thread?.members;
  const typingUsers = useMemo(() => {
    const users = typingForSelected ?? {};
    const nowMs = Date.now();
    return Object.entries(users)
      .filter(([id, value]) => id !== userId && value.until > nowMs)
      .map(([id]) => ({
        userId: id,
        // Display names come from the trusted member list, never from the socket payload.
        userName: threadMembers?.find((member) => String(member._id) === id)?.fullName ?? t("Someone"),
      }));
  }, [typingForSelected, userId, threadMembers, t]);

  const setScope = useCallback((next: ChatScope) => {
    setScopeState(next);
    try {
      sessionStorage.setItem(SCOPE_STORAGE_KEY, next);
    } catch {
      // Persistence is best-effort.
    }
  }, []);

  const toggleUnreadOnly = useCallback(() => setUnreadOnly((prev) => !prev), []);

  const close = () => {
    dispatch(setShowPortalChat(false));
    setMaximized(false); // PRD: closing resets maximize
  };

  const contextValue: PortalChatContextValue = {
    userId,
    userName,
    conversations,
    sections,
    projectOrder,
    loadingList,
    connected,
    selectedId,
    thread,
    typingUsers,
    composerIntent,
    highlightMessageId,
    currentProjectId,
    scope,
    setScope,
    unreadOnly,
    toggleUnreadOnly,
    selectConversation,
    refreshList,
    loadOlderMessages,
    sendMessage,
    editMessage,
    deleteMessage,
    deleteAttachment,
    toggleReaction,
    setComposerIntent,
    setHighlightMessageId,
    updateConversationState,
    emitTyping,
    markConversationRead: markRead,
    openForwardDialog: setForwardMessage,
    openReceiptsDialog: setReceiptsMessage,
    openReactionsDialog: setReactionsMessage,
    openEditDialog: setEditDialogMessage,
    unhideAndOpen,
    selectNextAfter,
    menuEpoch,
    jumpToMessage,
    showToast,
    showServerError,
  };

  if (!show || !userId) return null;

  return (
    <PortalChatContext.Provider value={contextValue}>
      <div
        data-testid="portalChatPanel"
        className={`fixed z-[1200] flex flex-col overflow-hidden rounded-lg border border-[#dce6f1] bg-white shadow-[0_1.25rem_2.5rem_0_rgba(106,138,177,0.35)] ${
          maximized
            ? "inset-x-0 bottom-0 top-[4.7rem] rounded-none"
            : "bottom-3.5 end-3.5 top-[5.5rem] w-[65.375rem] max-w-[calc(100vw-1.75rem)]"
        }`}
      >
        <div className="flex items-center justify-between border-b border-[#e8f1fc] bg-[#f0f5f9] px-4 py-2">
          <button
            type="button"
            data-testid="portalChatScope"
            className="flex items-center gap-1 text-base font-semibold"
            onClick={(e) => setScopeMenuAnchor(e.currentTarget)}
          >
            {scope === "all"
              ? t("All Chats")
              : scope === "direct"
                ? t("Direct Chats")
                : scope === "group"
                  ? t("Group Chats")
                  : t("Project Chats")}
            <IconComponent name="BottomArrow" width="0.7rem" height="0.7rem" />
          </button>
          <div className="flex items-center gap-3">
            {!connected && (
              <span className="rounded bg-[#fff3e0] px-2 py-0.5 text-xs text-[#b26a00]">{t("Reconnecting…")}</span>
            )}
            <IconComponent
              name={maximized ? "exitFullScreen" : "enterFullScreen"}
              width="1.1rem"
              height="1.1rem"
              toolTipContent={maximized ? t("Restore panel") : t("Maximise panel")}
              onClick={() => {
                setMaximized((prev) => !prev);
                setMenuEpoch((prev) => prev + 1); // PRD §4.6: open menus close on maximise/restore
                setScopeMenuAnchor(null);
              }}
            />
            <IconComponent name="CrossIcon" width="1rem" height="1rem" toolTipContent={t("Close")} onClick={close} />
          </div>
        </div>
        {scopeMenuAnchor && (
          <MenuComponent anchorEl={scopeMenuAnchor} open onClose={() => setScopeMenuAnchor(null)} width="12rem">
            {(["all", "direct", "group", "project"] as const).map((option) => (
              <MenuItemComponent
                key={option}
                onClick={() => {
                  setScope(option);
                  setScopeMenuAnchor(null);
                }}
              >
                {option === "all"
                  ? t("All Chats")
                  : option === "direct"
                    ? t("Direct Chats")
                    : option === "group"
                      ? t("Group Chats")
                      : t("Project Chats")}
              </MenuItemComponent>
            ))}
          </MenuComponent>
        )}
        <div className="flex min-h-0 flex-1">
          <ChatListPane />
          <ChatThreadPane />
        </div>
      </div>
      {forwardMessage && <ForwardDialog message={forwardMessage} onClose={() => setForwardMessage(null)} />}
      {receiptsMessage && <ReceiptsDialog message={receiptsMessage} onClose={() => setReceiptsMessage(null)} />}
      {reactionsMessage && <ReactionsDialog message={reactionsMessage} onClose={() => setReactionsMessage(null)} />}
      {editDialogMessage && (
        <EditMessageDialog message={editDialogMessage} onClose={() => setEditDialogMessage(null)} />
      )}
    </PortalChatContext.Provider>
  );
}

export default PortalChatPanel;
